<template>
  <div class="et-lane" :class="{ 'et-lane--fill-width': fillWidth }">
    <div class="et-lane__label" :style="labelColumnStyle">
      <p class="et-lane__label-main">{{ label }}</p>
      <p v-if="sublabel" class="et-lane__label-sub">{{ sublabel }}</p>
    </div>

    <div class="et-lane__track" :style="trackStyle">
      <template v-if="status === 'error'">
        <div class="et-lane__error">
          <p class="et-lane__error-text">Couldn’t load this lane</p>
          <button type="button" class="et-lane__retry" @click="$emit('retry')">
            Retry
          </button>
        </div>
      </template>

      <template v-else-if="loading || status === 'loading'">
        <div class="et-lane__skeleton-row">
          <span
            v-for="(w, i) in skeletonWidthsPrimary"
            :key="`sk-${i}`"
            class="et-lane__skeleton"
            :style="{ width: `${w}px` }"
          />
        </div>
      </template>

      <template v-else-if="isEmpty">
        <div class="et-lane__empty">{{ emptyText }}</div>
      </template>

      <template v-else>
        <div class="et-lane__stack">
          <TimelineLaneRow
            v-for="row in rows"
            :key="rowStableKey(row, 'row')"
            :segments="row"
            :selectable="selectable"
            :selected-id="selectedId"
            :hovered-id="hoveredId"
            :today-ms="todayMs"
            @select="$emit('bar-select', $event)"
            @hover="(id, isHovered) => $emit('bar-hover', id, isHovered)"
          />
        </div>
      </template>
    </div>
  </div>
</template>

<script setup>
import { computed } from "vue";
import TimelineLaneRow from "./TimelineLaneRow.vue";

const props = defineProps({
  label: { type: String, required: true },
  sublabel: { type: String, default: "" },
  labelColumnStyle: { type: Object, default: () => ({}) },
  trackStyle: { type: Object, default: () => ({}) },
  status: { type: String, default: "ready" },
  loading: { type: Boolean, default: false },
  emptyText: { type: String, default: "No records" },
  rows: { type: Array, default: () => [] },
  selectable: { type: Boolean, default: false },
  selectedId: { type: [String, Number], default: null },
  hoveredId: { type: [String, Number], default: null },
  todayMs: { type: Number, default: null },
  fillWidth: { type: Boolean, default: false },
});

defineEmits(["bar-select", "retry", "bar-hover"]);

const skeletonWidthsPrimary = [180, 120, 200];

function hasRowContent(rows) {
  return (rows || []).some((row) => Array.isArray(row) && row.length > 0);
}

function rowStableKey(row, prefix) {
  if (!row?.length) return `${prefix}-empty`;
  return `${prefix}-${row.map((segment) => segment.key).join("_")}`;
}

const isEmpty = computed(() => !hasRowContent(props.rows));
</script>

<style scoped>
.et-lane {
  display: flex;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid #eaecf0;
}

.et-lane--fill-width {
  width: 100%;
}

.et-lane--fill-width .et-lane__track {
  flex: 1 1 0;
  min-width: 0;
}

.et-lane__label {
  flex-shrink: 0;
  padding-right: 16px;
}

.et-lane__label-main {
  margin: 0;
  font-size: 12px;
  font-weight: 600;
  line-height: normal;
  color: #344054;
}

.et-lane__label-sub {
  margin: 2px 0 0;
  font-size: 11px;
  font-weight: 400;
  line-height: normal;
  color: #667085;
}

.et-lane__track {
  position: relative;
  flex-shrink: 0;
  /* Fallback for browsers without overflow-x: clip; the 3px bar ring then stays clipped. */
  overflow: hidden;
  overflow-x: clip;
  overflow-y: visible;
}

.et-lane__stack {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.et-lane__empty {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 24px;
  padding: 0 8px;
  border: 1px dashed #d0d5dd;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 400;
  color: #98a2b3;
  text-align: center;
}

.et-lane__error {
  display: flex;
  gap: 12px;
  align-items: center;
  justify-content: center;
  height: 24px;
  padding: 0 8px;
  border: 1px dashed #fda29b;
  border-radius: 4px;
  background: #fef3f2;
}

.et-lane__error-text {
  margin: 0;
  font-size: 11px;
  color: #b42318;
}

.et-lane__retry {
  padding: 0;
  border: none;
  background: none;
  font-size: 11px;
  font-weight: 600;
  color: #b42318;
  text-decoration: underline;
  cursor: pointer;
}

.et-lane__skeleton-row {
  display: flex;
  gap: 8px;
  height: 24px;
  align-items: center;
}

.et-lane__skeleton {
  display: block;
  height: 24px;
  border-radius: 4px;
  background: #eaecf0;
}
</style>
